import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchMembershipCatalog,
  startMembershipCheckout,
  type MembershipPlan,
} from './membershipService';
import {
  formatMembershipMoney,
  getPlanDetailExtra,
  type BillingInterval,
} from './planDetails';
import { savePendingMembership } from './membershipStatus';
import { useAuth } from '../../contexts/AuthContext';
import './MembershipPricingPage.css';

export const MembershipGetStartedPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { client } = useAuth();
  const clientId = client?.client_id || null;
  const planId = params.get('plan') || '';
  const billingParam = (params.get('billing') === 'annual' ? 'annual' : 'monthly') as BillingInterval;
  const paymentState = params.get('payment');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<MembershipPlan | null>(null);
  const [billing, setBilling] = useState<BillingInterval>(billingParam);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMembershipCatalog();
        if (cancelled) return;
        const found = (data.plans || []).find((p) => p.plan_id === planId) || null;
        if (!found) {
          setError('That plan could not be found. Please choose a plan again.');
        } else {
          setPlan(found);
        }
      } catch {
        if (!cancelled) setError('Unable to load plan details right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  const extra = useMemo(
    () => (plan ? getPlanDetailExtra(plan.tier) : null),
    [plan]
  );

  const price = plan
    ? billing === 'monthly'
      ? plan.monthly_amount
      : plan.annual_amount
    : 0;

  const handleConfirm = async () => {
    if (!plan || submitting) return;
    if (!clientId) {
      setError('Please sign in again to continue checkout.');
      return;
    }
    setSubmitting(true);
    setError('');

    const perkLabels = [...(plan.perks || [])]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => p.label);

    savePendingMembership(clientId, {
      plan_id: plan.plan_id,
      plan_name: plan.name,
      plan_subtitle: plan.subtitle,
      tier: plan.tier,
      billing,
      service_discount_percent: plan.service_discount_percent,
      perk_labels: perkLabels,
    });

    try {
      const successUrl = `${window.location.origin}/membership?payment=success&plan=${encodeURIComponent(plan.plan_id)}&billing=${billing}`;
      const cancelUrl = `${window.location.origin}/membership/get-started?plan=${encodeURIComponent(plan.plan_id)}&billing=${billing}&payment=cancelled`;

      const data = await startMembershipCheckout({
        plan_id: plan.plan_id,
        billing,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      if (!data.sessionUrl) {
        throw new Error(data.message || 'Unable to start Stripe checkout');
      }
      window.location.href = data.sessionUrl;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Unable to start checkout');
      setError(message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mp-page is-ready">
        <div className="mp-atmosphere" aria-hidden />
        <div className="mp-loading">
          Preparing your plan
          <div className="mp-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  if (!plan || !extra) {
    return (
      <div className="mp-page is-ready">
        <div className="mp-atmosphere" aria-hidden />
        <div className="mp-shell">
          <div className="mp-empty">
            <h1>Get started</h1>
            <p>{error || 'Choose a membership plan to continue.'}</p>
            <button
              type="button"
              className="mp-text-link"
              onClick={() => navigate('/membership')}
            >
              Back to plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-page is-ready">
      <div className="mp-atmosphere" aria-hidden />
      <div className="mp-shell mp-gs-shell">
        <button
          type="button"
          className="mp-gs-back"
          onClick={() => navigate('/membership')}
        >
          ← Membership plans
        </button>

        <header className="mp-header mp-gs-header">
          <h1 className="mp-title">Get started with {plan.name}</h1>
          <p className="mp-subtitle">
            Confirm billing, then continue to Stripe Checkout to pay{' '}
            {formatMembershipMoney(price, plan.currency)}
            {billing === 'monthly' ? '/mo' : '/yr'}.
          </p>
        </header>

        <div className={`mp-gs-card mp-gs-card--${plan.theme}`}>
          <div className="mp-gs-card-top">
            <div>
              <h2>{plan.name}</h2>
              <p>{plan.subtitle}</p>
            </div>
            <div className="mp-gs-price">
              <span key={billing}>{formatMembershipMoney(price, plan.currency)}</span>
              <small>{billing === 'monthly' ? '/mo' : '/yr'}</small>
            </div>
          </div>

          <div className="mp-billing" role="group" aria-label="Billing interval">
            <button
              type="button"
              className={`mp-billing-btn${billing === 'monthly' ? ' is-active' : ''}`}
              onClick={() => setBilling('monthly')}
              disabled={submitting}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`mp-billing-btn${billing === 'annual' ? ' is-active' : ''}`}
              onClick={() => setBilling('annual')}
              disabled={submitting}
            >
              Annual
            </button>
          </div>

          <ul className="mp-gs-highlights">
            {extra.highlights.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>

          <p className="mp-gs-discount">
            Includes <strong>{plan.service_discount_percent}% off</strong> eligible
            service orders once active.
          </p>

          {paymentState === 'cancelled' && (
            <div className="mp-error">Checkout was cancelled. You can try again below.</div>
          )}
          {error && <div className="mp-error">{error}</div>}

          <button
            type="button"
            className={`mp-cta mp-cta--${plan.theme}`}
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting
              ? 'Redirecting to Stripe…'
              : `Pay ${formatMembershipMoney(price, plan.currency)} with Stripe`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembershipGetStartedPage;
